import { brand, activeThemeId } from "../../config/theme";
import { useState, useEffect, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";
import { useQueryClient } from "@tanstack/react-query";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useResellerCounts } from "../../features/reseller/counts";
import { useStatuses } from "../../features/catalogs/queries";
import { incrementAdminFetchGeneration } from "../../utils/adminFetchGeneration";
import { setAdminAbortSignal } from "../../utils/api";
import { useUserStore } from "../../store/userStore";
import { useActiveAppStore } from "../../store/activeAppStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";
import { faGrip } from "@fortawesome/free-solid-svg-icons";
import { isMenuItemEnabled } from "../../config/appsConfig";
import { useVehiclesSubscription } from "../../hooks/useVehiclesSubscription";
import Swal from "sweetalert2";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import CompanySelector from "../../components/base/CompanySelector";
import { ADMIN_RESELLER_ROLE_ID } from "../../hooks/useIsReseller";
import { WialonTokenModal } from "./components/sections/wialon-devices/components/WialonTokenModal";

export default function ResellerAdminLayout() {
  const { t } = useTranslation("common");
  const { themeMode, isDarkMode, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);

  const {
    isExpiringSoon,
    timeRemainingFormatted,
    isExpired,
    isAuthenticated,
    isInitialized,
  } = useSessionCheck({
    warningThreshold: 60 * 60 * 1000,
    autoRefresh: true,
  });

  useVehiclesSubscription();

  // Companies store for count and selector
  const allCompanies = useCompaniesStore((state) => state.allCompanies);
  const allCompaniesLoading = useCompaniesStore((state) => state.allCompaniesLoading);
  const fetchAllCompanies = useCompaniesStore((state) => state.fetchAllCompanies);
  
  // Company selector state
  const companyId = useCompaniesStore((state) => state.companyId);
  const setCompanyId = useCompaniesStore((state) => state.setCompanyId);
  const companyUser = useCompaniesStore((state) => state.companyUser);
  const setCompanyUser = useCompaniesStore((state) => state.setCompanyUser);
  
  // Verificar si es reseller por ROL (no por compañía)
  const isReseller = (localStorage.getItem("userRoleId") || "") === ADMIN_RESELLER_ROLE_ID;
  
  const previousCompanyId = useRef<string | null | undefined>(undefined);
  const abortControllerRef = useRef<AbortController | null>(null);

  useStatuses();

  const countsQuery = useResellerCounts();
  const counts = countsQuery.data;
  const countsLoading = countsQuery.isLoading;

  const companiesCount = allCompanies.length;
  const usersCount = counts?.users ?? 0;
  const devicesCount = counts?.devices ?? 0;
  const brandsCount = counts?.models ?? 0;
  const integratorsCount = counts?.integrators ?? 0;
  const providersCount = counts?.providers ?? 0;

  // Los conteos de Mapon y Wialon alimentaban los menús que #327 quitó (siguen
  // comentados abajo). Se calculaban igual: 4 consultas a las integraciones en
  // cada carga del panel (todas las unidades y usuarios de Mapon) para nada.
  // Si se reactivan esos menús, volver a pedirlos con useMaponUnits/useMaponUsers
  // y useWialonDevices/useWialonUsers de features/reseller/integrations.

  const queryClient = useQueryClient();
  const [showWialonTokenModal, setShowWialonTokenModal] = useState(false);
  const [wialonToken, setWialonToken] = useState(() => {
    return localStorage.getItem('wialonToken') || '';
  });

  const handleSaveWialonToken = (token: string) => {
    setWialonToken(token);
    localStorage.setItem('wialonToken', token);
    queryClient.invalidateQueries({ queryKey: ["wialonDevices"] });
    queryClient.invalidateQueries({ queryKey: ["wialonUsers"] });
  };
    
  const avisosCount = counts?.avisos ?? 0;

  // Set active app
  const setActiveApp = useActiveAppStore((state) => state.setActiveApp);
  useEffect(() => {
    setActiveApp(
      "reseller-admin",
      t("apps.reseller-admin.name"),
      t("apps.reseller-admin.description"),
      faGrip,
      "bg-blue-600"
    );
  }, [setActiveApp, t]);

  // Sincronizar companyId y companyUser desde localStorage al montar
  useEffect(() => {
    const storedCompanyId = localStorage.getItem("companyId");
    const storedCompanyUser = localStorage.getItem("userCompanyId");

    if (storedCompanyId && storedCompanyId !== companyId) {
      setCompanyId(storedCompanyId);
    }

    if (storedCompanyUser && storedCompanyUser !== companyUser) {
      setCompanyUser(storedCompanyUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar todas las compañías para el selector
  useEffect(() => {
    fetchAllCompanies();
  }, [fetchAllCompanies]);

  // Cleanup abort signal on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setAdminAbortSignal(null);
    };
  }, []);

  useEffect(() => {
    if (
      previousCompanyId.current === companyId &&
      previousCompanyId.current !== undefined
    ) {
      return;
    }
    previousCompanyId.current = companyId;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    setAdminAbortSignal(abortControllerRef.current.signal);
    incrementAdminFetchGeneration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Handle session expiration
  useEffect(() => {
    if (
      isInitialized &&
      (isExpired || !isAuthenticated) &&
      !hasRedirected.current
    ) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  // Redirect to splash if no user
  useEffect(() => {
    if (!user) {
      if (location.pathname !== "/splash") {
        localStorage.setItem("lastRoute", location.pathname);
        navigate("/splash");
      }
    }
  }, [user, location.pathname, navigate]);
  useEffect(() => {
    if (location.pathname === "/reseller-admin" || location.pathname === "/reseller-admin/") {
      const defaultPath =
        activeThemeId === "central-gps"
          ? "/reseller-admin/reseller-users"
          : "/reseller-admin/companies";
      navigate(defaultPath, { replace: true });
    }
  }, [location.pathname, navigate]);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>(["v5", "mapon", "wialon"]);
  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const toggleSubmenu = (menuId: string) => {
    setExpandedMenus((prev) =>
      prev.includes(menuId)
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId]
    );
  };

  const menuItems = [
    {
      id: "v5",
      label: "V5",
      icon: "ri-database-2-line",
      path: "",
      children: [
        {
          id: "companies",
          label: "Compañías",
          icon: "ri-building-line",
          path: "/reseller-admin/companies",
          count: companiesCount,
          loading: allCompaniesLoading,
        },
        {
          id: "devices",
          label: "Dispositivos",
          icon: "ri-cpu-line",
          path: "/reseller-admin/devices",
          count: devicesCount,
          loading: countsLoading,
          children: [
            {
              id: "models",
              label: "Modelos de Dispositivos",
              icon: "ri-cpu-line",
              path: "/reseller-admin/models",
              count: brandsCount,
              loading: countsLoading,
            },
          ],
        },
        {
          id: "users",
          label: "Usuarios",
          icon: "ri-user-line",
          path: "/reseller-admin/users",
          count: usersCount,
          loading: countsLoading,
          // children comentado: en el diseño anterior Usuarios no tenía submenú.
          // Se reactivará en una modificación posterior.
          // children: [
          //   {
          //     id: "user-roles",
          //     label: "User Roles",
          //     icon: "ri-shield-user-line",
          //     path: "/reseller-admin/user-roles",
          //   },
          // ],
        },
        {
          id: "integrators",
          label: t("integrators.menuTitle", { ns: "resellerAdmin", defaultValue: "Integradores" }),
          icon: "ri-plug-line",
          path: "/reseller-admin/integrators",
          count: integratorsCount,
          loading: countsLoading,
        },
        {
          id: "providers",
          label: t("providers.menuTitle", { ns: "resellerAdmin", defaultValue: "Proveedores" }),
          icon: "ri-cloud-line",
          path: "/reseller-admin/providers",
          count: providersCount,
          loading: countsLoading,
        },
      ],
      dividerAfter: true,
    },
    // {
    //   id: "mapon",
    //   label: "Mapon",
    //   icon: "ri-database-2-line",
    //   path: "",
    //   children: [
    //     {
    //       id: "mapon-devices",
    //       label: "Dispositivos",
    //       icon: "ri-cpu-line",
    //       path: "/reseller-admin/mapon-devices",
    //       count: maponDevicesCount,
    //       loading: maponDevicesLoading,
    //     },
    //     {
    //       id: "mapon-users",
    //       label: "Usuarios",
    //       icon: "ri-user-line",
    //       path: "/reseller-admin/mapon-users",
    //       count: maponUsersCount,
    //       loading: maponUsersLoading,
    //     },
    //   ],
    //   dividerAfter: true,
    // },
    // {
    //   id: "wialon",
    //   label: "Wialon",
    //   icon: "ri-database-2-line",
    //   path: "",
    //   children: [
    //     {
    //       id: "wialon-devices",
    //       label: "Dispositivos",
    //       icon: "ri-cpu-line",
    //       path: "/reseller-admin/wialon-devices",
    //       count: wialonDevicesCount,
    //       loading: wialonDevicesLoading,
    //     },
    //     {
    //       id: "wialon-users",
    //       label: "Usuarios",
    //       icon: "ri-user-line",
    //       path: "/reseller-admin/wialon-users",
    //       count: wialonUsersCount,
    //       loading: wialonUsersLoading,
    //     },
    //     {
    //       id: "wialon-token-config",
    //       label: "Configurar Token",
    //       icon: "ri-settings-3-line",
    //       path: "#",
    //       action: () => setShowWialonTokenModal(true),
    //     },
    //   ],
    //   dividerAfter: true,
    // },
    {
      id: "reseller-users",
      label: "Usuarios Reseller",
      icon: "ri-user-star-line",
      path: "/reseller-admin/reseller-users",
      children: [
        {
          id: "reseller-user-roles",
          label: "User Roles",
          icon: "ri-shield-user-line",
          path: "/reseller-admin/reseller-user-roles",
        },
      ],
      dividerAfter: true,
    },
    {
      id: "licenses",
      label: "Licencias",
      icon: "ri-key-2-line",
      path: "/reseller-admin/licenses",
    },
    {
      id: "avisos",
      label: t("sidebarMenu.avisos"),
      icon: "ri-megaphone-line",
      path: "/reseller-admin/avisos",
      count: avisosCount,
      loading: countsLoading,
    },
  ];

  // Filter menu items based on app config
  const filterMenuItem = (item: any) => {
    if (!isMenuItemEnabled("internos", "reseller-admin", item.id)) {
      return false;
    }
    if (item.children) {
      item.children = item.children.filter((child: any) =>
        isMenuItemEnabled("internos", "reseller-admin", child.id)
      );
    }
    return true;
  };

  const filteredMenuItems = menuItems.filter(filterMenuItem);

  const handleNavigation = (path: string) => {
    navigate(path);
    closeMobileMenu();
  };

  const handleAppSelect = (app: AppItem) => {
    if (app.id === "reseller-admin") {
      setIsAppsSidebarOpen(false);
      return;
    }
    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.route) {
      setIsAppsSidebarOpen(false);
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className={`h-screen flex flex-col ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}>
      {/* Session expiring banner */}
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>
            {t("sessionBanner.expiring", { time: timeRemainingFormatted })}
          </span>
        </div>
      )}
      <Header
        isDarkMode={isDarkMode}
        onToggleMobileMenu={toggleMobileMenu}
        isMobileMenuOpen={isMobileMenuOpen}
        onOpenAppsSidebar={() => setIsAppsSidebarOpen(true)}
        isReseller={isReseller}
        companies={allCompanies}
        selectedCompany={companyId}
        onCompanyChange={setCompanyId}
      />

      <div className="flex flex-1 min-h-0 overflow-hidden relative">
        <AppSidebarMenu
          menuItems={filteredMenuItems}
          isDarkMode={isDarkMode}
          isMobileMenuOpen={isMobileMenuOpen}
          onCloseMobileMenu={closeMobileMenu}
          onNavigate={handleNavigation}
          isActiveRoute={(_itemId, path) =>
            !!path && (location.pathname === path || location.pathname.startsWith(path + "/"))
          }
          companySelector={
            isReseller ? (
              <CompanySelector
                value={companyId ?? null}
                onChange={(id) => setCompanyId(id)}
                isDarkMode={isDarkMode}
                isClearable
                companies={allCompanies.length > 0 ? allCompanies : undefined}
                className="w-full"
              />
            ) : undefined
          }
          companyIndicator={{
            companyId,
            companyName:
              allCompanies.find((c) => c.id === companyId)?.name ||
              allCompanies.find((c) => c.id === companyId)?.business_name ||
              companyId,
            isReseller,
            isDarkMode,
          }}
          expandedMenus={expandedMenus}
          onToggleSubmenu={toggleSubmenu}
        />
        {/* Main Content */}
        <div className="flex-1 max-h-full overflow-hidden min-w-0 w-full">
          <Outlet />
        </div>
      </div>

      {/* Wialon Token Modal */}
      <WialonTokenModal
        isOpen={showWialonTokenModal}
        onClose={() => setShowWialonTokenModal(false)}
        onSave={handleSaveWialonToken}
        currentToken={wialonToken}
        themeClasses={{
          bg: isDarkMode ? "bg-dark-surface" : "bg-white",
          border: isDarkMode ? "border-dark-border" : "border-gray-200",
          text: isDarkMode ? "text-white" : "text-gray-900",
          textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
          cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
          cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
          buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
          buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
          buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
        }}
      />

      {/* Apps Sidebar */}
      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={() => setIsAppsSidebarOpen(false)}
        categories={filteredCategories}
        selectedAppId="reseller-admin"
        onAppSelect={handleAppSelect}
        isDarkMode={isDarkMode}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={user ? {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          username: user.username,
        } : undefined}
        onProfileClick={() => {
          setIsAppsSidebarOpen(false);
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
